/* kott-predictive-search
 *
 * Search popup that shows results while typing (shell in
 * snippets/kott-search-overlay.liquid, data from
 * sections/kott-predictive-search.liquid).
 *
 * Shopify's predictive search API rejects Hebrew, and its storefront search is
 * too loose for a popup: it answers almost any input with something, and ranks
 * a half-typed second word badly ("אבקת חל" puts beet powder above protein
 * powder). So the popup downloads a compact index of the catalogue once and
 * searches it here: every key is answered without a request, and what counts
 * as a match is decided by the rules below instead of by fuzzy relevance.
 * Storefront search is only asked when the index has nothing (the words may
 * still be in a description) or could not be loaded.
 */
(function () {
  var root = document.getElementById('KottSearch');
  if (!root) return;

  var SECTION_ID = 'kott-predictive-search';
  var TRIGGERS = '.js-search-header, [data-kott-search-open]';
  var CACHE_TTL = 15 * 60 * 1000;
  var DEBOUNCE = 150;
  // Above this many index pages (250 products each) the index is too heavy to
  // ship to a phone; storefront search takes over.
  var MAX_INDEX_PAGES = 8;
  var IMAGE_WIDTHS = [180, 270, 360, 540];
  var NIQQUD = /[\u0591-\u05C7]/g;
  var QUOTES = /['"`\u2019\u05F3\u05F4]/g;
  var NOT_LETTER = /[^0-9a-z\u05D0-\u05EA]+/;

  var input = root.querySelector('.kott-search__input');
  var form = root.querySelector('.kott-search__form');
  var layer = root.querySelector('.kott-search__layer');
  var body = root.querySelector('[data-kott-search-body]');
  var searchUrl = root.getAttribute('data-search-url');
  var indexUrl = root.getAttribute('data-index-url');
  var indexPages = parseInt(root.getAttribute('data-index-pages'), 10) || 0;
  var unavailable = root.getAttribute('data-unavailable');
  var storageKey = 'kottSearch:' + root.getAttribute('data-cache-key');
  // words that send a product to the end of the results (catalogues, samples)
  var demoteWords = tokens(root.getAttribute('data-demote') || '');
  var mobile = window.matchMedia('(max-width: 768px)');

  var els = null;
  var lists = { suggestions: [], collections: [] };
  var bodyPromise = null;
  var index = null;
  var indexPromise = null;
  var fallback = {};
  var request = null;
  var timer = null;
  var closeTimer = null;
  var lastFocus = null;

  function limit(name) {
    return parseInt(root.getAttribute('data-limit-' + name), 10) || 0;
  }

  function resultsUrl(term) {
    return searchUrl + '?q=' + encodeURIComponent(term) + '&options%5Bprefix%5D=last';
  }

  function fetchSection(url, signal) {
    return fetch(url, { signal: signal, credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('kott-search ' + response.status);
        return response.text();
      })
      .then(function (text) {
        var section = new DOMParser().parseFromString(text, 'text/html').getElementById('shopify-section-' + SECTION_ID);
        if (!section) throw new Error('kott-search section missing');
        return section;
      });
  }

  function readStored(name) {
    if (window.Shopify && Shopify.designMode) return null;
    try {
      var stored = JSON.parse(sessionStorage.getItem(storageKey + name));
      if (stored && Date.now() - stored.time < CACHE_TTL) return stored.value;
    } catch (e) {}
    return null;
  }

  function store(name, value) {
    try {
      sessionStorage.setItem(storageKey + name, JSON.stringify({ time: Date.now(), value: value }));
    } catch (e) {}
  }

  /* ---------- Matching ---------- */

  // Final letters, niqqud and quote marks are typing noise, not meaning.
  function normalize(text) {
    return text.toLowerCase()
      .replace(NIQQUD, '')
      .replace(QUOTES, '')
      .replace(/ך/g, 'כ').replace(/ם/g, 'מ').replace(/ן/g, 'נ').replace(/ף/g, 'פ').replace(/ץ/g, 'צ');
  }

  function tokens(text) {
    return normalize(text).split(NOT_LETTER).filter(Boolean);
  }

  // אבקת / אבקה / אבקות should meet on the same stem.
  function stem(word) {
    return word.length > 3 ? word.replace(/(ימ|ות|ה|ת)$/, '') : word;
  }

  // ו/ה/ב/ל/מ/ש/כ glued to the front of a word, one or two of them:
  // "וספירולינה", "ולתינוק"
  function withoutPrefix(word) {
    var rest = [];
    if (/^[והבלמשכ]/.test(word) && word.length > 3) {
      rest.push(word.slice(1));
      if (/^[והבלמשכ]{2}/.test(word) && word.length > 4) rest.push(word.slice(2));
    }
    return rest;
  }

  // Hebrew spells the same word with or without י / ו: מסכה = מסיכה. True when
  // `full` is `short` with only such letters added inside it. With `prefix`,
  // `short` may also stop early (still being typed). Two different vowels are
  // two different words: ספיר is not סופר.
  function isVowelVariant(short, full, prefix) {
    var matched = 0;
    for (var i = 0; i < full.length; i++) {
      if (matched === short.length) return prefix;
      if (full.charAt(i) === short.charAt(matched)) matched++;
      else if (full.charAt(i) !== 'י' && full.charAt(i) !== 'ו') return false;
    }
    return matched === short.length;
  }

  function sameButVowels(a, b) {
    if (a.length === b.length) return false;
    return a.length < b.length ? isVowelVariant(a, b, false) : isVowelVariant(b, a, false);
  }

  // How well a typed word matches a word of a title or label; 0 = not at all.
  function wordScore(term, word) {
    if (word === term) return 3;
    if (word.indexOf(term) === 0) return 2.5;

    var rest = withoutPrefix(word);
    if (rest.some(function (part) { return part.indexOf(term) === 0; })) return 2;

    // same stem, not "starts with the stem": שינה must not find שיניים
    var termStem = stem(term);
    if ([word].concat(rest).some(function (part) { return stem(part) === termStem; })) return 1.5;

    if (term.length > 3) {
      if (sameButVowels(term, word) || (termStem.length > 2 && sameButVowels(termStem, stem(word)))) return 1.25;
      // still typing: היאלרונ... for היאלורונית
      if (term.length > 4 && isVowelVariant(term, word, true)) return 1.25;
    }
    return 0;
  }

  function bestScore(term, words) {
    var best = 0;
    for (var i = 0; i < words.length && best < 3; i++) {
      best = Math.max(best, wordScore(term, words[i]));
    }
    return best;
  }

  // Edit distance with a swap of two neighbouring letters counted as one edit.
  function distance(a, b) {
    var rows = [];
    for (var i = 0; i <= a.length; i++) {
      rows[i] = [i];
      for (var j = 1; j <= b.length; j++) {
        if (i === 0) {
          rows[i][j] = j;
          continue;
        }
        var cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
        var value = Math.min(rows[i - 1][j] + 1, rows[i][j - 1] + 1, rows[i - 1][j - 1] + cost);
        if (i > 1 && j > 1 && a.charAt(i - 1) === b.charAt(j - 2) && a.charAt(i - 2) === b.charAt(j - 1)) {
          value = Math.min(value, rows[i - 2][j - 2] + 1);
        }
        rows[i][j] = value;
      }
    }
    return rows[a.length][b.length];
  }

  // A typo: one wrong letter in a word of 5+, two in a word of 9+, never the
  // first letter. Shorter words have too many real neighbours (שינה / שיני).
  // From 6 letters on, the title word may be longer than what is typed so far.
  function isTypoOf(term, word) {
    if (term.length < 5) return false;
    var allowed = term.length >= 9 ? 2 : 1;
    if (word.charAt(0) !== term.charAt(0) || word.length < term.length - allowed) return false;
    return distance(term, word) <= allowed ||
      (term.length >= 6 && word.length > term.length && distance(term, word.slice(0, term.length)) <= allowed);
  }

  // Titles read "name | detail | form". Each word remembers which part it is in.
  function titleWords(title) {
    var words = [];
    title.split('|').forEach(function (part, partIndex) {
      tokens(part).forEach(function (word) {
        words.push({ word: word, part: partIndex });
      });
    });
    return words;
  }

  // How a product answers the typed words: how many are plainly in its title,
  // how many are matched at all (also a spelling variant or typo of a title
  // word, or type/tags/handle), how long those matched words are, and a score.
  //
  // The score: a word in the name part of the title counts for much more than
  // one after a "|" ("קריאטין" is about "קריאטין מונוהידראט | אבקה", not about
  // a collagen mix that lists creatine as an ingredient), and one of the first
  // two words a little more again ("אצת ספירולינה", "חמאת קקאו" are the thing
  // itself; "שמן זרעי מורינגה" is made from it). Both bonuses shrink with a
  // looser match, so a same-stem word in a name never beats the exact word
  // elsewhere. Products that score the same are ordered by sales (see
  // selectProducts).
  function evaluate(queryTokens, product) {
    var words = product.titleWords;
    var inTitle = 0;
    var matched = 0;
    var weight = 0;
    var score = 0;

    queryTokens.forEach(function (term) {
      var points = 0;
      var at = -1;
      var i;
      for (i = 0; i < words.length; i++) {
        var wordPoints = wordScore(term, words[i].word);
        if (wordPoints > points) {
          points = wordPoints;
          at = i;
        }
      }
      if (points >= 1.5) {
        inTitle++;
      } else if (!points) {
        for (i = 0; i < words.length && at < 0; i++) {
          if (isTypoOf(term, words[i].word)) {
            points = 0.75;
            at = i;
          }
        }
      }

      if (at > -1) {
        matched++;
        weight += term.length;
        score += points + ((words[at].part === 0 ? 2 : 0.5) + (at < 2 ? 0.5 : 0)) * points / 3;
      } else if (bestScore(term, product.termTokens)) {
        matched++;
        weight += term.length;
        score += 1;
      }
    });

    // the typed words appear together, in that order
    if (queryTokens.length > 1 && product.titleText.indexOf(queryTokens.join(' ')) > -1) score += 1.5;

    return { product: product, inTitle: inTitle, matched: matched, weight: weight, score: score };
  }

  // Products for the typed words, the most exact group only: every word plainly
  // in the title; else every word matched somehow; else the products matching
  // the most words. Nothing matched -> empty.
  function selectProducts(queryTokens, products) {
    var count = queryTokens.length;
    // a demote word stops demoting once the shopper is typing that very word
    var demoting = demoteWords.filter(function (word) {
      return !queryTokens.some(function (term) { return word.indexOf(term) === 0; });
    });
    var evaluated = products
      .filter(function (product) { return unavailable !== 'hide' || product.available; })
      .map(function (product) {
        var entry = evaluate(queryTokens, product);
        entry.demoted = product.demote.some(function (word) { return demoting.indexOf(word) > -1; });
        return entry;
      });

    var pool = evaluated.filter(function (entry) { return entry.inTitle === count; });
    if (!pool.length) pool = evaluated.filter(function (entry) { return entry.matched === count; });
    if (!pool.length) {
      var most = evaluated.reduce(function (max, entry) { return Math.max(max, entry.matched); }, 0);
      if (most) {
        pool = evaluated.filter(function (entry) { return entry.matched === most; });
        // of those, only the ones that matched the longest words:
        // "מי ורדים" is about ורדים, not about everything starting with מי
        var heaviest = pool.reduce(function (max, entry) { return Math.max(max, entry.weight); }, 0);
        pool = pool.filter(function (entry) { return entry.weight === heaviest; });
        // and only if that is a real share of what was typed: the "3" of
        // "אומגה 3" alone says nothing
        var typed = queryTokens.join('').length;
        if (heaviest * 2.5 < typed) pool = [];
      }
    }

    return pool
      .sort(function (a, b) {
        if (a.demoted !== b.demoted) return a.demoted ? 1 : -1;
        if (b.score !== a.score) return b.score - a.score;
        if (unavailable === 'last' && a.product.available !== b.product.available) return a.product.available ? -1 : 1;
        // the index arrives best-selling first
        return a.product.order - b.product.order;
      })
      .map(function (entry) { return entry.product; });
  }

  // Suggestions and categories: every typed word must be in the label.
  function selectLabels(queryTokens, items, labelOf) {
    return items
      .map(function (item, order) {
        var words = tokens(labelOf(item));
        var score = 0;
        var all = queryTokens.every(function (term) {
          var points = bestScore(term, words);
          score += points;
          return points > 0;
        });
        return { item: item, order: order, score: score, all: all };
      })
      .filter(function (entry) { return entry.all; })
      .sort(function (a, b) { return b.score - a.score || a.order - b.order; })
      .map(function (entry) { return entry.item; });
  }

  /* ---------- Products ---------- */

  // [title, url, image, terms, available, price, compare_at, body_match]
  // from snippets/kott-search-product.liquid
  function toProduct(row, order) {
    var titleTokens = tokens(row[0]);
    return {
      title: row[0],
      url: row[1],
      image: row[2],
      available: row[4],
      price: row[5],
      compareAt: row[6],
      bodyMatch: row[7],
      titleWords: titleWords(row[0]),
      titleText: titleTokens.join(' '),
      demote: demoteWords.filter(function (word) { return titleTokens.indexOf(word) > -1; }),
      // the handle often carries the English name: ...-creatine-berry-pure
      termTokens: tokens((row[3] || '') + ' ' + row[1].replace(/^.*\/products\//, '').replace(/\?.*$/, '')),
      order: order
    };
  }

  function readProducts(section) {
    return JSON.parse(section.querySelector('[data-kott-search-products]').textContent);
  }

  function sized(image, width) {
    return image.replace(/width=\d+/, 'width=' + width);
  }

  function buildCard(product) {
    var card = document.createElement('a');
    card.className = 'kott-search__card';
    card.href = product.url;
    card.setAttribute('data-kott-search-item', '');

    var media = document.createElement('span');
    media.className = 'kott-search__card-media';
    if (product.image) {
      var img = document.createElement('img');
      img.className = 'kott-search__card-img';
      img.alt = product.title;
      img.loading = 'lazy';
      img.decoding = 'async';
      // sizes and srcset before src, or the browser fetches src first
      img.sizes = '(max-width: 768px) 45vw, 226px';
      img.srcset = IMAGE_WIDTHS.map(function (width) { return sized(product.image, width) + ' ' + width + 'w'; }).join(', ');
      img.src = sized(product.image, 360);
      media.appendChild(img);
    }
    card.appendChild(media);

    var title = document.createElement('span');
    title.className = 'kott-search__card-title';
    title.textContent = product.title;
    card.appendChild(title);

    if (product.price) {
      var price = document.createElement('span');
      price.className = 'kott-search__card-price';
      // money strings rendered by Liquid; the "from" one carries a <span>
      price.innerHTML = product.price + (product.compareAt ? ' <s class="kott-search__card-compare">' + product.compareAt + '</s>' : '');
      card.appendChild(price);
    }
    return card;
  }

  function fillGrid(grid, products) {
    grid.textContent = '';
    products.forEach(function (product) {
      grid.appendChild(buildCard(product));
    });
  }

  function loadIndexPage(page) {
    return fetchSection(indexUrl + '?section_id=' + SECTION_ID + '&sort_by=best-selling&page=' + page).then(readProducts);
  }

  // Resolves to true when the index is usable; never rejects.
  function loadIndex() {
    if (indexPromise) return indexPromise;

    var stored = readStored(':index');
    if (stored) {
      index = stored.map(toProduct);
      indexPromise = Promise.resolve(true);
      return indexPromise;
    }

    if (!indexPages || indexPages > MAX_INDEX_PAGES) {
      indexPromise = Promise.resolve(false);
      return indexPromise;
    }

    // all pages at once: the wait is one page long, not the sum of them
    var pages = [];
    for (var page = 1; page <= indexPages; page++) pages.push(loadIndexPage(page));
    indexPromise = Promise.all(pages)
      .then(function (results) {
        var rows = results.reduce(function (all, rows) { return all.concat(rows); }, []);
        store(':index', rows);
        index = rows.map(toProduct);
        return true;
      })
      .catch(function () {
        return false;
      });
    return indexPromise;
  }

  /* ---------- Popup body ---------- */

  function mountBody(html) {
    body.innerHTML = html;
    els = {
      initial: body.querySelector('[data-kott-search-initial]'),
      emptyGrid: body.querySelector('[data-kott-search-empty-grid]'),
      results: body.querySelector('[data-kott-search-results]'),
      none: body.querySelector('[data-kott-search-none]'),
      noneTerm: body.querySelector('[data-kott-search-none-term]'),
      side: body.querySelector('[data-kott-search-side]'),
      suggestions: body.querySelector('[data-kott-search-suggestions]'),
      collections: body.querySelector('[data-kott-search-collections]'),
      viewAll: body.querySelector('[data-kott-search-view-all]'),
      grid: body.querySelector('[data-kott-search-grid]')
    };
    try {
      lists = JSON.parse(body.querySelector('[data-kott-search-data]').textContent);
    } catch (e) {}
    if (els.emptyGrid) fillGrid(els.emptyGrid, (lists.empty || []).map(toProduct));
    fitEmptyGrid();
  }

  // Before typing, desktop shows one full row of products: however many fit
  // the width, never a second row with one or two left over.
  function fitEmptyGrid() {
    // only measurable while it is on screen; show() calls back when it returns
    if (!els || !els.emptyGrid || root.hidden || els.initial.hidden) return;
    var cards = els.emptyGrid.children;
    var i;
    for (i = 0; i < cards.length; i++) cards[i].hidden = false;
    if (mobile.matches) return;
    var columns = getComputedStyle(els.emptyGrid).gridTemplateColumns.split(' ').length;
    for (i = columns; i < cards.length; i++) cards[i].hidden = true;
  }

  function loadBody() {
    if (bodyPromise) return bodyPromise;

    var stored = readStored(':body');
    if (stored) {
      mountBody(stored);
      bodyPromise = Promise.resolve();
      return bodyPromise;
    }

    bodyPromise = fetchSection(searchUrl + '?section_id=' + SECTION_ID)
      .then(function (section) {
        store(':body', section.innerHTML);
        mountBody(section.innerHTML);
      })
      .catch(function (error) {
        bodyPromise = null;
        throw error;
      });
    return bodyPromise;
  }

  function show(state) {
    els.initial.hidden = state !== 'initial';
    els.results.hidden = state !== 'results';
    els.none.hidden = state !== 'none';
    // results take the whole screen, the other states only what they need
    root.classList.toggle('kott-search--full', state === 'results');
    if (state === 'initial') fitEmptyGrid();
  }

  /* ---------- Rendering ---------- */

  function fillList(container, entries) {
    var ul = container.querySelector('ul');
    ul.textContent = '';
    entries.forEach(function (entry) {
      var li = document.createElement('li');
      var a = document.createElement('a');
      a.href = entry.url;
      a.textContent = entry.label;
      a.setAttribute('data-kott-search-item', '');
      li.appendChild(a);
      ul.appendChild(li);
    });
    container.hidden = !entries.length;
  }

  function renderLists(queryTokens) {
    var suggestions = selectLabels(queryTokens, lists.suggestions, function (term) { return term; })
      .slice(0, limit('suggestions'))
      .map(function (term) { return { label: term, url: resultsUrl(term) }; });

    var collections = selectLabels(queryTokens, lists.collections, function (collection) { return collection[0]; })
      .slice(0, limit('collections'))
      .map(function (collection) { return { label: collection[0], url: collection[1] }; });

    fillList(els.suggestions, suggestions);
    fillList(els.collections, collections);
    els.side.hidden = !suggestions.length && !collections.length;
  }

  function renderProducts(term, products) {
    root.classList.remove('is-loading');
    if (!products.length) {
      els.noneTerm.textContent = term;
      show('none');
      return;
    }
    fillGrid(els.grid, products.slice(0, limit(mobile.matches ? 'mobile' : 'desktop')));
    els.viewAll.href = resultsUrl(term);
    show('results');
    layer.scrollTop = 0;
  }

  // The lists are already current; the grid waits with a spinner.
  function renderWaiting(term) {
    root.classList.add('is-loading');
    els.grid.textContent = '';
    els.viewAll.href = resultsUrl(term);
    show('results');
  }

  /* ---------- Searching ---------- */

  function cancelPending() {
    clearTimeout(timer);
    if (request) request.abort();
    request = null;
  }

  // Storefront search, for words the index does not have. Its results are kept
  // only if they pass the same title rules or really contain every typed word.
  function searchStorefront(term, queryTokens) {
    function finish(products) {
      var picked = selectProducts(queryTokens, products);
      if (!picked.length) {
        picked = products.filter(function (product) {
          return product.bodyMatch && (unavailable !== 'hide' || product.available);
        });
      }
      renderProducts(term, picked);
    }

    if (fallback[term]) {
      finish(fallback[term]);
      return;
    }

    request = new AbortController();
    var current = request;
    var url = searchUrl + '?section_id=' + SECTION_ID + '&q=' + encodeURIComponent(term) +
      '&type=product&options%5Bprefix%5D=last&options%5Bunavailable_products%5D=' + unavailable;
    fetchSection(url, current.signal)
      .then(function (section) {
        fallback[term] = readProducts(section).map(toProduct);
        if (current !== request) return;
        request = null;
        finish(fallback[term]);
      })
      .catch(function (error) {
        if (error.name === 'AbortError' || current !== request) return;
        request = null;
        renderProducts(term, []);
      });
  }

  function update() {
    var term = input.value.trim();
    cancelPending();

    loadBody().then(function () {
      // the field may have changed while the body was loading
      if (term !== input.value.trim()) return;

      var queryTokens = tokens(term);
      if (!queryTokens.length) {
        root.classList.remove('is-loading');
        show('initial');
        return;
      }

      renderLists(queryTokens);
      if (!index) renderWaiting(term);

      loadIndex().then(function () {
        if (term !== input.value.trim()) return;

        var products = index ? selectProducts(queryTokens, index) : [];
        if (products.length) {
          renderProducts(term, products);
          return;
        }
        if (fallback[term]) {
          searchStorefront(term, queryTokens);
          return;
        }
        renderWaiting(term);
        timer = setTimeout(function () { searchStorefront(term, queryTokens); }, DEBOUNCE);
      });
    }).catch(function () {});
  }

  /* ---------- Open / close ---------- */

  // iOS keeps the layout viewport full height under the keyboard, which would
  // leave the last results unreachable; the visual viewport is what is visible.
  function fitViewport() {
    layer.style.maxHeight = window.visualViewport.height + 'px';
  }

  function warm() {
    loadBody().catch(function () {});
    loadIndex();
  }

  function open() {
    clearTimeout(closeTimer);
    lastFocus = document.activeElement;
    root.hidden = false;
    // top layer where <dialog> is supported; plain fixed overlay elsewhere
    if (root.showModal && !root.open) root.showModal();
    document.documentElement.classList.add('kott-search-open');
    if (window.visualViewport) {
      fitViewport();
      window.visualViewport.addEventListener('resize', fitViewport);
    }
    // reflow so the fade-in runs from opacity 0
    root.offsetWidth;
    root.classList.add('is-open');
    input.focus();
    warm();
    fitEmptyGrid();
  }

  function close() {
    if (root.hidden) return;
    cancelPending();
    root.classList.remove('is-open', 'is-loading');
    document.documentElement.classList.remove('kott-search-open');
    if (window.visualViewport) window.visualViewport.removeEventListener('resize', fitViewport);
    clearTimeout(closeTimer);
    closeTimer = setTimeout(function () {
      if (root.open && root.close) root.close();
      root.hidden = true;
      input.value = '';
      if (els) show('initial');
      layer.scrollTop = 0;
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }, 200);
  }

  function visibleItems() {
    return Array.prototype.filter.call(root.querySelectorAll('[data-kott-search-item]'), function (el) {
      return el.getClientRects().length;
    });
  }

  // Capture phase: the theme's own header search handler must not run as well.
  document.addEventListener('click', function (event) {
    if (event.target.closest(TRIGGERS)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      open();
      return;
    }
    if (event.target.closest('[data-kott-search-close]')) {
      event.preventDefault();
      close();
    }
  }, true);

  // Reaching for the search starts the downloads before the tap lands. A mouse
  // passing over the icon only fetches the small popup body, not the index.
  function isTrigger(event) {
    return event.target.closest && event.target.closest(TRIGGERS + ', .kott-mobile-search-bar');
  }
  document.addEventListener('pointerover', function (event) {
    if (isTrigger(event)) loadBody().catch(function () {});
  }, { passive: true });
  document.addEventListener('touchstart', function (event) {
    if (isTrigger(event)) warm();
  }, { passive: true });

  input.addEventListener('input', update);
  window.addEventListener('resize', fitEmptyGrid);

  form.addEventListener('submit', function (event) {
    event.preventDefault();
    var term = input.value.trim();
    if (term) window.location.href = resultsUrl(term);
  });

  root.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') {
      close();
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      var items = visibleItems();
      if (!items.length) return;
      var position = items.indexOf(document.activeElement);
      var next = event.key === 'ArrowDown' ? position + 1 : position - 1;
      event.preventDefault();
      if (next < 0 || next >= items.length) input.focus();
      else items[next].focus();
    }
  });

  // Escape on a modal dialog: run our own close so the state stays in step.
  root.addEventListener('cancel', function (event) {
    event.preventDefault();
    close();
  });

  // Coming back with the browser's back button must not land on an open popup.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) close();
  });

  // Hand over from the inline listener in the snippet, and replay a tap on the
  // search icon that came before this script had loaded.
  document.removeEventListener('click', window.kottSearchEarly, true);
  if (window.kottSearchPending) {
    window.kottSearchPending = false;
    open();
  }
})();
